import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase/firebaseConfig';

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const { user } = useAuth();

  // 🔁 Fetch cart from Firestore on login
  useEffect(() => {
    const loadCart = async () => {
      if (!user) return;
      try {
        const userRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(userRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCartItems(data.cart || []);
        }
      } catch (err) {
        console.error("❌ Failed to fetch cart from Firestore:", err);
      }
    };
    loadCart();
  }, [user]);

  // 💾 Save minimal cart to Firestore on every cart change
  useEffect(() => {
    const saveCart = async () => {
      if (!user) return;

      const cleanedCart = cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        mainImage: item.mainImage || '',
      }));

      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          await setDoc(userRef, {
            userId: user.uid,
            createdAt: new Date().toISOString(),
            cart: cleanedCart
          });
          console.log("🆕 Created user document with cart");
        } else {
          await setDoc(userRef, { cart: cleanedCart }, { merge: true });
          console.log("✅ Updated cart in Firestore:", cleanedCart);
        }
      } catch (err) {
        console.error("❌ Failed to save cart to Firestore:", err);
      }
    };

    saveCart();
  }, [cartItems, user]);

  const normalize = (items) => items.filter(item => item.quantity > 0);

  const addToCart = (product, quantity = 1) => {
    const minimalProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity,
      mainImage: product.mainImage || '',
    };

    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      let updated;
      if (existing) {
        updated = prev.map(item =>
          item.id === product.id ? { ...item, quantity } : item
        );
      } else {
        updated = [...prev, minimalProduct];
      if (window.gtag) {
        window.gtag('event', 'add_to_cart', {
          currency: 'INR',
          value: product.price * quantity,
          items: [
            {
              item_id: product.id,
              item_name: product.name,
              quantity: quantity,
              price: product.price,
            },
          ],
        });
      }
    }
      return normalize(updated);
    });
  };

  const removeFromCart = (id) => {
  const removedItem = cartItems.find(item => item.id === id);

  if (window.gtag && removedItem) {
    window.gtag('event', 'remove_from_cart', {
      currency: 'INR',
      value: removedItem.price * removedItem.quantity,
      items: [
        {
          item_id: removedItem.id,
          item_name: removedItem.name,
          quantity: removedItem.quantity,
          price: removedItem.price,
        },
      ],
    });
  }

  setCartItems(prev => normalize(prev.filter(item => item.id !== id)));
};


  const updateQuantity = (id, delta) => {
    setCartItems(prev => {
      const updated = prev.map(item =>
        item.id === id
          ? { ...item, quantity: item.quantity + delta }
          : item
      );
      return normalize(updated);
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  // ❌ Clear cart on logout
  useEffect(() => {
    if (!user) {
      setCartItems([]);
    }
  }, [user]);

  return (
    <CartContext.Provider
      value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};

